import { Circle, Wifi, WifiHigh, WifiLow } from 'lucide-react';
import { PoweredByLogo } from '../public/PoweredByLogo';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { formatLiveDuration } from '@/lib/formatLiveDuration';
import { Separator } from '@/components/ui/separator';
import { measureConnectionTier, toDisplayTier } from '@/lib/connectionQuality';

interface FooterBarProps {
  visualIdentity?: WorkspaceVisualIdentity;
  currentTime?: number;
}

export default function FootBarReplay({ visualIdentity, currentTime = 0 }: FooterBarProps) {
  const [connectionQuality, setConnectionQuality] = useState<'high' | 'medium' | 'low'>('high');

  useEffect(() => {
    const testConnection = async () => {
      try {
        const quality = await measureConnectionTier();
        // Sonde non concluante (bloqueur) : garder le badge affiché plutôt que annoncer « faible ».
        if (quality !== null) setConnectionQuality(toDisplayTier(quality));
      } catch (error) {
        console.error('Erreur lors du test de connexion:', error);
        setConnectionQuality('low');
      }
    };

    testConnection();

    const interval = setInterval(testConnection, 30000);

    return () => clearInterval(interval);
  }, []);

  const renderWifiIcon = () => {
    const iconProps = { size: 24 };

    switch (connectionQuality) {
      case 'high':
        return <WifiHigh {...iconProps} color="green" />;
      case 'medium':
        return <Wifi {...iconProps} color="green" />;
      case 'low':
        return <WifiLow {...iconProps} color="red" />;
      default:
        return <Wifi {...iconProps} color="green" />;
    }
  };

  const isDark = visualIdentity?.theme === 'DARK';
  const bgClass = isDark ? 'bg-[var(--color-dark)]' : 'bg-white';
  const borderClass = isDark ? 'border-[var(--color-card-inverse)]' : 'border-[var(--color-card)]';
  const separatorBgClass = isDark ? 'bg-[var(--color-card-inverse)]' : 'bg-[var(--color-card)]';
  const textClass = isDark ? 'text-white' : 'text-[var(--color-secondary)]';

  return (
    <div className="w-full flex items-center justify-between ">
      <PoweredByLogo
        theme={visualIdentity?.theme as 'LIGHT' | 'DARK'}
        classNameText="text-sm"
        classNameLogo="w-16"
      />

      <div
        className={cn(
          'h-8 p-2 px-4 shadow-card rounded-full flex items-center justify-center gap-4 border',
          bgClass,
          borderClass,
        )}
      >
        <div>
          <p className={cn('flex items-center justify-center gap-2 text-sm', textClass)}>
            <Circle className="w-4 h-4" fill="red" stroke="red" />
            {formatLiveDuration(currentTime)}
          </p>
        </div>

        <Separator orientation="vertical" className={cn('h-full', separatorBgClass)} />

        {renderWifiIcon()}
      </div>
    </div>
  );
}
